import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { JwtTokenBase, provideJwtAuth, StorageType } from '@devlearning/jwt-auth';
import { MutexFastLockModule } from '@devlearning/mutex-fast-lock';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import moment from 'moment';
import { exhaustMap, map, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { GlobalErrorHandler } from './core/global-error-handler';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { ApplicationInsightsService } from './core/services/application-insights.service';
import { CacheTransportHubService } from './core/services/cache-transport-hub.service';
import { EnumsService } from './core/services/enums.service';
import { GoogleApiService } from './core/services/google-api.service';
import { LanguageService } from './core/services/language.service';
import { UserService } from './core/services/user.service';
import { provideStore, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import localeEn from '@angular/common/locales/en';
import { appReducer } from './store/app/app.reducer';
import { AuthApplicationCodes } from './core/models/auth-application-code';
import type { AuthenticationToken } from './core/models/authentication-token';

registerLocaleData(localeIt);
registerLocaleData(localeEn);

const jwtAuthConfig = {
  logLevel: 1,
  tokenUrl: environment.TOKEN_URL,
  refreshUrl: environment.REFRESH_URL,
  useManualInitialization: true,
  storageType: StorageType.SESSION_STORAGE,
  refreshTokenRequestFactory: (token: JwtTokenBase) => ({
    authApplicationCode: AuthApplicationCodes.Repro,
    username: (token as AuthenticationToken).username,
    refreshToken: (token as AuthenticationToken).refreshToken,
  }),
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    // { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } },
    importProvidersFrom(
      MutexFastLockModule.forRoot({
        lockPrefix: '_MUTEX_LOCK',
        timeout: 30000,
        debugEnabled: false,
      }),
      StoreModule.forRoot(
        {
          app: appReducer,
        },
        {
          metaReducers: [],
        },
      ),
      EffectsModule.forRoot([]),
    ),
    provideTranslateService({
      defaultLanguage: environment.DEFAULT_LANGUAGE,
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideJwtAuth(jwtAuthConfig),
    provideStoreDevtools({
      name: 'layover-app-admin',
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeAppFactory(
        inject(LanguageService),
        inject(UserService),
        inject(EnumsService),
        inject(CacheTransportHubService),
        inject(GoogleApiService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      inject(ApplicationInsightsService);
    }),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: sessionStorage.getItem('locale') || 'it-IT',
    },
    provideAnimations(),
  ],
};

export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) { }

  getTranslation(lang: string): Observable<{ [key: string]: string }> {
    console.debug(`Loading translation file for lang ${lang}`);
    return this.http.get<{ [key: string]: string }>(`/assets/i18n/${lang}.json`);
  }
}

export function translateLoaderFactory(http: HttpClient): TranslateLoader {
  return new CustomTranslateLoader(http);
}

// Factory per l'inizializzazione dell'app
function initializeAppFactory(
  language: LanguageService,
  user: UserService,
  enums: EnumsService,
  cacheTransportHub: CacheTransportHubService,
  googleApi: GoogleApiService,
): () => Observable<any> {
  return () =>
    user.initialize().pipe(
      exhaustMap((x) => language.initialize(x.userData?.language!)),
      tap((x) => console.info('Lang', x.languageCultureName)),
      tap((x) => moment.locale(x.languageCultureName?.substring(0, 2))),
      exhaustMap((x) => enums.initialize()),
      exhaustMap((x) => cacheTransportHub.initialize()),
      tap((x) => googleApi.initialize(environment.googleApiKey, 'it-IT')),
      map((x) => true),
      tap((x) => console.info('Initialization completed')),
    );
}

// {
//   provide: MAT_DIALOG_DEFAULT_OPTIONS,
//   useValue: { panelClass: 'mat-dialog-override' }
// },
// {
//   provide: HTTP_INTERCEPTORS,
//   useClass: HttpRequestAddIanaNameInterceptor,
//   multi: true
// },
